(() => {
  const CHUNK_SIZE = 5 * 1024 * 1024;
  const MAX_VIDEO_BYTES = 2 * 1024 * 1024 * 1024;
  const MAX_PHOTO_BYTES = 50 * 1024 * 1024;
  const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.m4v', '.webm', '.avi'];
  const PHOTO_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
  const MIN_VIDEO_SHORT_EDGE = 720;
  const MIN_VIDEO_LONG_EDGE = 1280;
  const FULL_HD_SHORT_EDGE = 1080;
  const FULL_HD_LONG_EDGE = 1920;
  const root = document.querySelector('[data-vip-upload-root]');

  if (!root) return;

  const q = (selector) => root.querySelector(selector);
  const api = (root.dataset.apiBaseUrl || '').replace(/\/$/, '');
  const shopUrl = (root.dataset.shopUrl || location.origin).replace(/\/$/, '');
  const form = q('[data-vip-form]');
  const err = q('[data-vip-error]');
  const progressWrap = q('[data-vip-progress]');
  const progressFill = q('[data-vip-progress-fill]');
  const progressText = q('[data-vip-progress-text]');
  const progressHeading = q('[data-vip-progress-heading]');
  const progressMessage = q('[data-vip-progress-message]');
  const progressSpinner = q('[data-vip-spinner]');
  const thankYou = q('[data-vip-thankyou]');
  const codeEl = q('[data-vip-code]');
  const couponLabel = q('[data-vip-coupon-label]');
  const couponNote = q('[data-vip-coupon-note]');
  const shopBtn = q('[data-vip-shop]');
  const copyBtn = q('[data-vip-copy]');
  const qualityWarning = q('[data-vip-quality-warning]');
  const params = new URLSearchParams(location.search);
  let submissionInFlight = false;
  let qualityCheckId = 0;
  let qualityInspection = { file: null, promise: null };

  if (params.get('name')) form.first_name.value = params.get('name');
  if (params.get('email')) form.email.value = params.get('email');

  const showError = (message) => {
    err.hidden = false;
    err.textContent = message;
  };

  const clearError = () => {
    err.hidden = true;
    err.textContent = '';
  };

  const clearQualityWarning = () => {
    qualityWarning.hidden = true;
    qualityWarning.textContent = '';
    delete qualityWarning.dataset.level;
  };

  const readVideoDimensions = (file) => new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const probe = document.createElement('video');
    let settled = false;

    const finish = (dimensions = null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      probe.removeAttribute('src');
      probe.load();
      URL.revokeObjectURL(objectUrl);
      resolve(dimensions);
    };

    const timeout = setTimeout(() => finish(), 8000);
    probe.preload = 'metadata';
    probe.muted = true;
    probe.onloadedmetadata = () => {
      if (!probe.videoWidth || !probe.videoHeight) {
        finish();
        return;
      }
      finish({
        width: probe.videoWidth,
        height: probe.videoHeight,
      });
    };
    probe.onerror = () => finish();
    probe.playsInline = true;
    probe.src = objectUrl;
  });

  const classifyVideoQuality = (dimensions) => {
    if (!dimensions) return null;

    const shortEdge = Math.min(dimensions.width, dimensions.height);
    const longEdge = Math.max(dimensions.width, dimensions.height);
    if (shortEdge < MIN_VIDEO_SHORT_EDGE || longEdge < MIN_VIDEO_LONG_EDGE) {
      return {
        level: 'blocked',
        message: `Your video came through at reduced quality — please upload it from your Photo Library so we get the full-quality version. This file is ${dimensions.width}×${dimensions.height}; we need at least 1280×720.`,
      };
    }

    if (shortEdge < FULL_HD_SHORT_EDGE || longEdge < FULL_HD_LONG_EDGE) {
      return {
        level: 'warning',
        message: `This video is ${dimensions.width}×${dimensions.height}. It will upload, but it may look softer than the full-quality version. For the clearest result, upload the original from your Photo Library.`,
      };
    }

    return { level: 'clear', message: '' };
  };

  const getVideoQuality = (file) => {
    if (qualityInspection.file === file && qualityInspection.promise) {
      return qualityInspection.promise;
    }

    const promise = readVideoDimensions(file)
      .then(classifyVideoQuality)
      .catch(() => null);
    qualityInspection = { file, promise };
    return promise;
  };

  const renderQualityFeedback = (quality) => {
    clearQualityWarning();
    if (!quality || quality.level === 'clear') return;
    qualityWarning.dataset.level = quality.level;
    qualityWarning.textContent = quality.message;
    qualityWarning.hidden = false;
  };

  const checkVideoQuality = async () => {
    const file = form.video.files[0];
    const checkId = ++qualityCheckId;
    clearQualityWarning();
    if (!file || !VIDEO_EXTENSIONS.includes(fileExtension(file))) return;

    const quality = await getVideoQuality(file);
    if (checkId !== qualityCheckId || form.video.files[0] !== file) return;
    renderQualityFeedback(quality);
  };

  form.video.addEventListener('change', checkVideoQuality);

  const fileExtension = (file) => {
    const dot = file.name.lastIndexOf('.');
    return dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
  };

  const sessionKey = (file, kind) => [
    'malezia-vip-upload',
    kind,
    file.name,
    file.size,
    file.lastModified,
  ].join(':');

  const getStoredSession = (file, kind) => {
    try {
      return JSON.parse(localStorage.getItem(sessionKey(file, kind)) || 'null');
    } catch {
      return null;
    }
  };

  const storeSession = (file, kind, session) => {
    localStorage.setItem(sessionKey(file, kind), JSON.stringify(session));
  };

  const clearSession = (file, kind) => {
    localStorage.removeItem(sessionKey(file, kind));
  };

  const completionKey = (file) => [
    'malezia-vip-completion',
    form.email.value.trim().toLowerCase(),
    file.name,
    file.size,
    file.lastModified,
  ].join(':');

  const getIdempotencyKey = (file) => {
    const key = completionKey(file);
    const stored = localStorage.getItem(key);
    if (stored) return stored;

    const idempotencyKey = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${crypto.getRandomValues(new Uint32Array(4)).join('-')}`;
    localStorage.setItem(key, idempotencyKey);
    return idempotencyKey;
  };

  const setFormBusy = (busy) => {
    submissionInFlight = busy;
    form.setAttribute('aria-busy', String(busy));
    [...form.elements].forEach((control) => {
      control.disabled = busy;
    });
  };

  const showUploadingState = () => {
    progressHeading.textContent = 'Uploading your video...';
    progressMessage.textContent = "Please don't close this page. Depending on your connection this can take a few minutes for longer videos.";
    progressSpinner.hidden = true;
  };

  const showFinalizingState = () => {
    setProgress(1, 1);
    progressHeading.textContent = '✅ Upload complete — finalizing your submission';
    progressMessage.textContent = "Please don't close this page. This can take up to a minute.";
    progressSpinner.hidden = false;
  };

  const setProgress = (uploaded, total) => {
    const pct = total ? Math.max(0, Math.min(100, Math.round((uploaded / total) * 100))) : 0;
    progressFill.style.width = `${pct}%`;
    progressText.textContent = `${pct}%`;
    progressFill.parentElement.setAttribute('aria-valuenow', String(pct));
  };

  async function startSession(file, kind) {
    const cached = getStoredSession(file, kind);
    if (cached && cached.sessionUrl && cached.objectPath) return cached;

    const response = await fetch(`${api}/api/v1/upload-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: file.name,
        size: file.size,
        contentType: file.type || 'application/octet-stream',
        kind,
      }),
    });

    if (!response.ok) throw new Error('Unable to start upload session.');

    const session = await response.json();
    storeSession(file, kind, session);
    return session;
  }

  async function queryUploadedBytes(sessionUrl, fileSize) {
    const response = await fetch(sessionUrl, {
      method: 'PUT',
      headers: {
        'Content-Range': `bytes */${fileSize}`,
      },
    });

    if (response.ok) return fileSize;
    if (response.status !== 308) return 0;

    const range = response.headers.get('Range');
    return range ? Number(range.split('-')[1]) + 1 : 0;
  }

  async function uploadResumable(file, kind, progressOffset, progressSpan, totalProgressBytes) {
    const session = await startSession(file, kind);
    let start = await queryUploadedBytes(session.sessionUrl, file.size);

    setProgress(progressOffset + Math.min(start, file.size) * progressSpan, totalProgressBytes);

    while (start < file.size) {
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      let response;

      for (let attempt = 1; attempt <= 4; attempt += 1) {
        try {
          response = await fetch(session.sessionUrl, {
            method: 'PUT',
            headers: {
              'Content-Range': `bytes ${start}-${end - 1}/${file.size}`,
            },
            body: chunk,
          });
        } catch {
          response = null;
        }

        if (response && (response.ok || response.status === 308)) break;
        if (attempt === 4) {
          throw new Error('We lost connection to the server. Submit again to resume from the last uploaded chunk.');
        }

        await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
        start = await queryUploadedBytes(session.sessionUrl, file.size);
      }

      if (response.ok) {
        start = file.size;
      } else {
        const range = response.headers.get('Range');
        start = range ? Number(range.split('-')[1]) + 1 : end;
      }

      setProgress(progressOffset + Math.min(start, file.size) * progressSpan, totalProgressBytes);
    }

    clearSession(file, kind);
    return session.objectPath;
  }

  function validate(video, photos) {
    if (!video) return 'Please upload a video file.';
    if (!VIDEO_EXTENSIONS.includes(fileExtension(video))) return 'Please upload a video file. We accept .mp4, .mov, .m4v, .webm, and .avi.';
    if (video.size > MAX_VIDEO_BYTES) return 'Your video file is over 2 GB. Try recording at a lower resolution, or trim the video.';
    if (photos.length > 5) return 'Please upload no more than 5 photos.';
    if (photos.some((photo) => !PHOTO_EXTENSIONS.includes(fileExtension(photo)))) return 'Please upload photos as .jpg, .jpeg, .png, .heic, or .heif files.';
    if (photos.some((photo) => photo.size > MAX_PHOTO_BYTES)) return 'Each photo must be 50 MB or smaller.';
    if (!form.permission_granted.checked) return "Please check the permission box. We can't use your video without it.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.value.trim())) return 'Please enter a valid email address.';
    if (!form.first_name.value.trim()) return 'Please enter your first name.';
    if (!form.querySelector('input[name="usage_duration"]:checked')) return 'Please choose how long you have been using Malezia.';
    if (form.querySelectorAll('input[name="products_used"]:checked').length === 0) return 'Please choose at least one product.';
    return '';
  }

  function payload(videoObject, photoObjects) {
    return {
      first_name: form.first_name.value.trim(),
      email: form.email.value.trim(),
      products_used: [...form.querySelectorAll('input[name="products_used"]:checked')].map((item) => item.value),
      usage_duration: (form.querySelector('input[name="usage_duration"]:checked') || {}).value || '',
      permission_granted: form.permission_granted.checked,
      video_object: videoObject,
      photo_objects: photoObjects,
      idempotency_key: getIdempotencyKey(form.video.files[0]),
    };
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (submissionInFlight) return;
    submissionInFlight = true;
    clearError();

    if (!api || api.includes('PLACEHOLDER')) {
      submissionInFlight = false;
      showError('Submission is not active yet. Please check back shortly.');
      return;
    }

    const video = form.video.files[0];
    const photos = [...form.photos.files];
    const validation = validate(video, photos);

    if (validation) {
      submissionInFlight = false;
      showError(validation);
      return;
    }

    const quality = await getVideoQuality(video);
    if (form.video.files[0] !== video) {
      submissionInFlight = false;
      return;
    }
    renderQualityFeedback(quality);
    if (quality && quality.level === 'blocked') {
      submissionInFlight = false;
      showError('Please replace the reduced-quality video above before submitting.');
      form.video.focus({ preventScroll: true });
      form.video.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setFormBusy(true);
    progressWrap.hidden = false;
    showUploadingState();
    setProgress(0, 1);

    try {
      const totalUploadBytes = video.size + photos.reduce((total, photo) => total + photo.size, 0);
      let completedBytes = 0;
      const videoObject = await uploadResumable(video, 'video', completedBytes, 1, totalUploadBytes);
      completedBytes += video.size;

      const photoObjects = [];
      for (const photo of photos) {
        const photoObject = await uploadResumable(photo, 'photo', completedBytes, 1, totalUploadBytes);
        completedBytes += photo.size;
        photoObjects.push(photoObject);
      }

      showFinalizingState();

      const response = await fetch(`${api}/api/v1/submission-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload(videoObject, photoObjects)),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Something broke on our end. Please try again in a minute, or email care@malezia.com if it keeps failing.');
      }

      const result = await response.json();
      if (!result.discount_code || typeof result.discount_code !== 'string') {
        throw new Error('Your upload finished, but we could not confirm your thank-you code. Please try again in a minute, or email care@malezia.com.');
      }

      form.hidden = true;
      progressWrap.hidden = true;
      codeEl.textContent = result.discount_code;
      shopBtn.href = `${shopUrl}/?discount=${encodeURIComponent(result.discount_code)}`;
      if (result.coupon_reused) {
        couponLabel.textContent = 'Your existing 20% thank-you code';
        couponNote.textContent = "We saved your new story. Since one thank-you code is issued per email, we haven't created another. This is the original code on file for you; if you've already used it, it won't apply again.";
      }
      thankYou.hidden = false;
      root.classList.add('vip-upload--complete');
      thankYou.focus({ preventScroll: true });
      window.scrollTo({ top: 0, behavior: 'auto' });
      copyBtn.onclick = async () => {
        try {
          await navigator.clipboard.writeText(result.discount_code);
          copyBtn.textContent = 'Copied!';
        } catch {
          copyBtn.textContent = 'Copy failed';
        }
      };
    } catch (error) {
      setFormBusy(false);
      progressSpinner.hidden = true;
      progressWrap.hidden = true;
      showError(error.message || 'We lost connection to the server. Submit again to resume from the last uploaded chunk.');
      err.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
})();
